import React, { useEffect, useState } from "react";
import OtpInput from "react-otp-input";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "../components/Loading";

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 998,
  },
  container: {
    width: "550px",
    maxWidth: "90vw",
    padding: "20px",
    borderRadius: "30px",
    margin: "0 auto",
    backgroundColor: "#ffffff",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 999,
  },
  title: {
    fontSize: "1.2rem",
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: "4px",
    marginTop: "10px",
  },
  description: {
    textAlign: "center",
    fontSize: "0.9rem",
    color: "#5C5C5C",
    marginBottom: "20px",
    direction: "ltr",
  },
  otpInput: {
    display: "flex",
    direction: "ltr",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "20px",
    marginTop: "40px",
  },
  otpInputField: {
    width: "40px",
    height: "40px",
    fontSize: "1.5rem",
    border: "1px solid #ccc",
    borderRadius: "7px",
    textAlign: "center",
    margin: "0 5px",
    outline: "none",
    color: "black",
  },
  resendText: {
    textAlign: "center",
    color: "#5C5C5C",
    fontSize: "0.875rem",
    marginBottom: "20px",
  },
  resendButton: {
    fontWeight: "bold",
    color: "#1b4597",
    cursor: "pointer",
  },
  verifyButton: {
    width: "60%",
    padding: "10px",
    backgroundColor: "#1b4497",
    color: "white",
    borderRadius: "8px",
    cursor: "pointer",
    margin: "0 auto",
    display: "block",
    marginTop: "10px",
    marginBottom: "10px",
    fontWeight: "600",
  },
  cancelButton: {
    width: "60%",
    padding: "10px",
    backgroundColor: "transparent",
    color: "#1b4497",
    cursor: "pointer",
    margin: "0 auto",
    display: "block",
    fontWeight: "600",
  },
};

const VerifyContactOtp = ({
  isOpen,
  onClose,
  title,
  description,
  numeric = true,
  numInputs = 4,
  onResend,
  onSubmitOtp,
  onVerified,
}) => {
  const { t } = useTranslation();
  const [timer, setTimer] = useState(30);
  const [otpValue, setOtpValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [showIncorrectOTPMessage, setShowIncorrectOTPMessage] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setOtpValue("");
      setShowIncorrectOTPMessage(false);
      setTimer(30);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || timer <= 0) return;
    const timerId = setInterval(() => setTimer((prev) => prev - 1), 1000);
    return () => clearInterval(timerId);
  }, [timer, isOpen]);

  const handleVerify = async (value) => {
    const otp = value ?? otpValue;
    if (!otp || otp.length < numInputs) return;
    try {
      setLoading(true);
      const result = await onSubmitOtp(otp);
      if (result?.success) {
        setOtpValue("");
        onVerified?.();
      } else {
        setShowIncorrectOTPMessage(true);
      }
    } catch (error) {
      setShowIncorrectOTPMessage(true);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      setLoading(true);
      const result = await onResend?.();
      if (result?.success !== false) {
        setTimer(30);
        setOtpValue("");
        setShowIncorrectOTPMessage(false);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (otpValue.length === numInputs) {
      handleVerify(otpValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpValue]);

  if (!isOpen) return null;

  return (
    <>
      <div style={styles.overlay} onClick={onClose}></div>
      {loading && <LoadingSpinner />}
      <div style={styles.container}>
        <p style={styles.title}>{title}</p>
        <p style={styles.description}>{description}</p>
        <div style={styles.otpInput}>
          <OtpInput
            value={otpValue}
            onChange={(val) => {
              const cleaned = numeric ? val.replace(/\D/g, "") : val;
              setOtpValue(cleaned);
              setShowIncorrectOTPMessage(false);
            }}
            numInputs={numInputs}
            inputType={numeric ? "tel" : "text"}
            placeholder={" ".repeat(numInputs)}
            separator={<span>-</span>}
            renderInput={(props) => <input {...props} />}
            inputStyle={styles.otpInputField}
          />
        </div>
        <div>
          <p style={styles.resendText}>
            {t("not_received")}{" "}
            {timer > 0 ? (
              <span className="text-primaryBlue font-bold">
                {timer} {t("seconds")}
              </span>
            ) : (
              <span style={styles.resendButton} onClick={handleResend}>
                {t("resend")}
              </span>
            )}
          </p>
        </div>
        {showIncorrectOTPMessage && (
          <p className="flex justify-center text-md mt-[2vh] mb-3 text-red">
            {t("incorrectotp")}
          </p>
        )}
        <button
          type="button"
          style={styles.verifyButton}
          onClick={() => handleVerify()}
          disabled={otpValue?.length < numInputs}
        >
          {t("verify")}
        </button>
        <button type="button" style={styles.cancelButton} onClick={onClose}>
          {t("cancel")}
        </button>
      </div>
    </>
  );
};

export default VerifyContactOtp;
